import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { getNativeBilling, isNativeApp, storeLog, waitForNativeBilling } from "@/lib/store-billing";
import { restoreStorePurchases } from "@/lib/native-billing";

/**
 * Rechecks the Premium entitlement at app launch and whenever the app returns
 * to the foreground. Inside the native wrappers this also re-verifies the store
 * subscription so a renewal, cancellation or sandbox purchase made outside the
 * app is reflected without a restart.
 */
export function useEntitlementSync(): void {
  const { user, refreshPremium } = useAuth();

  useEffect(() => {
    // Web already refreshes on sign-in; the store round-trip is native-only.
    if (!user?.id || !isNativeApp()) return;
    let running = false;

    const sync = async (reason: string) => {
      if (running) return;
      running = true;
      try {
        // Only claim a store check when the StoreKit / Play bridge answered;
        // without it this is a plain server entitlement refresh.
        const bridge = getNativeBilling() ?? (await waitForNativeBilling());
        storeLog("entitlement_check", { reason, store_checked: Boolean(bridge) });
        if (bridge) await restoreStorePurchases();
        await refreshPremium();
      } catch {
        storeLog("entitlement_check_failed", { reason });
      } finally {
        running = false;
      }
    };

    void sync("launch");
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync("foreground");
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user?.id]);
}
